import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { agentService } from '../services/agentService';
import { AttachedAgentsPanel } from '../components/orchestrator/AttachedAgentsPanel';
import { OrchestratorChat } from '../components/orchestrator/OrchestratorChat';
import type { AgentDefinitionDto } from '../types/agent';

export const OrchestratorRunPage = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [agent, setAgent] = useState<AgentDefinitionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!agentId) return;
    setLoading(true);
    agentService
      .getAgent(agentId)
      .then(setAgent)
      .catch((err) => setError(err.message || 'Failed to load agent'))
      .finally(() => setLoading(false));
  }, [agentId]);

  if (loading) {
    return (
      <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !agent) {
    return (
      <Box sx={{ p: 6 }}>
        <Alert severity="error">{error || 'Agent not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Card sx={{ borderRadius: 0, boxShadow: 2 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/orchestrator')}
            variant="outlined"
            size="small"
          >
            Back
          </Button>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={600}>
              {agent.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {agent.slug} &middot; Multi-Agent Orchestrator
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* Main layout */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left rail: attached agents */}
        <Box
          sx={{
            width: 280,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
            p: 2,
            bgcolor: 'action.hover',
          }}
        >
          <AttachedAgentsPanel parentAgentId={agent.id} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Attached agents appear as callable tools to the orchestrator LLM. It can invoke
            them via tool calls to delegate sub-tasks.
          </Typography>
        </Box>

        {/* Main chat + timeline */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <OrchestratorChat agentId={agent.id} agentName={agent.name} />
        </Box>
      </Box>
    </Box>
  );
};
